import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Cookie } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    // Prüfe, ob der Benutzer den Cookie-Banner manuell öffnen möchte
    const showCookies = searchParams.get('showCookies')
    if (showCookies === 'true') {
      setIsVisible(true)
      // Entferne den Parameter aus der URL, ohne die Seite neu zu laden
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('showCookies')
        return newParams
      }, { replace: true })
      return
    }

    // Prüfe, ob der Benutzer bereits eine Cookie-Präferenz gespeichert hat
    const cookieConsent = localStorage.getItem('cookieConsent')
    if (!cookieConsent) {
      // Zeige den Banner nach kurzer Verzögerung
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [searchParams, setSearchParams])

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted')
    setIsVisible(false)
    window.dispatchEvent(new Event('jokic-cockpit-consent'))
  }

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined')
    setIsVisible(false)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="relative bg-gradient-to-br from-gray-900/95 to-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 p-6 md:p-8"
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none rounded-xl" />
              
              {/* Accent line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500 to-transparent" />
              
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-brand-600/20 to-brand-600/20 border border-brand-500/30">
                      <Cookie className="h-6 w-6 text-brand-400" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-serif text-xl md:text-2xl font-light text-white mb-2">
                      Cookie-Einstellungen
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                      Durch die Nutzung unserer Website stimmen Sie der Verwendung von Cookies zu. 
                      Weitere Informationen finden Sie in unserer{' '}
                      <a 
                        href="/datenschutz" 
                        className="text-brand-400 hover:text-brand-300 underline transition-colors"
                      >
                        Datenschutzerklärung
                      </a>.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <motion.button
                      onClick={handleDecline}
                      className="px-6 py-3 bg-transparent border border-white/20 text-white rounded-xl hover:border-white/30 hover:bg-white/5 transition-all duration-300 text-sm font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Ablehnen
                    </motion.button>
                    <motion.button
                      onClick={handleAccept}
                      className="px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-600 border border-brand-500/30 text-white rounded-xl hover:from-brand-500 hover:to-brand-500 transition-all duration-300 shadow-lg shadow-brand-600/20 text-sm font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Akzeptieren
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Close button */}
              <motion.button
                onClick={handleDecline}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="h-5 w-5" />
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}




